import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { Box, Typography, RichTextViewer, TextButton } from 'seagull';
import { Checkmark } from 'seagull/icons';
import { amendmentsTextsPath, getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from '~/business-logic/amendmentsLogic';
import { getServiceTermsEntries } from '~/client-flow/clientOb/ClientOnboardingInner/OnboardingPage/Steps/AdditionalTerms/additionalTerms.utils';
import { useViewLegalTerms } from '~/components/LegalTermsDropdown/hooks/useViewLegalTerms';
import { useShowQuillViewerModal } from '~/components/LegalTermsDropdown/QuillViewerModal/QuillViewerModal';
import { useQboClasses } from '~/hooks/reactQuery/queries/useQboClasses';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { PracticeManagementSyncFailures } from '~/integrations/PracticeManagement/PracticeManagementSyncFailures/PracticeManagementSyncFailures';
import { legalTermsModel, relationshipModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { customPagesEnabled } from '~/models/custom-page/model';
import { onAcceptanceTextsPath, paymentTermsTextsPath } from '~/models/relationship/selectors';
import styles from './AgreementView.module.scss';
import AgreementAmendmentsApprovalMethod from './components/AgreementAmendmentsApprovalMethod';
import AgreementInvoicesApprovalMethod from './components/AgreementInvoicesApprovalMethod';
import AmendmentChangesInAgreementView from './components/AmendmentChangesInAgreementView';
import ChangesSummary from './components/ChangesSummary';
import { AgreementViewCard, SpecificTerm } from './components/CommonComponents';
import { CustomPagesInAgreement } from './components/CustomPagesInAgreement/CustomPagesInAgreement';
import Header from './components/Header';
import SignedHeader from './components/SignedHeader';
import StatementOfWork from './components/StatementOfWork';
const autoprefix = 'agreement-view-';
export default function AgreementView(props) {
    var _relationship_customPages;
    var _relationship_practiceManagementSyncFailures;
    const { relationship, isVendor, refresh } = props;
    const { t: amendmentsTexts } = useLocaleKeys(amendmentsTextsPath);
    const { t: viewTexts } = useLocaleKeys('pages.agreementMinisite.agreementView');
    const { t: onAcceptanceTexts } = useLocaleKeys(onAcceptanceTextsPath);
    const { t: paymentTermsTexts } = useLocaleKeys(paymentTermsTextsPath);
    const featureFlags = useFeatureFlags();
    const customPagesOn = customPagesEnabled(!!featureFlags.CUSTOM_PAGES, (_relationship_customPages = relationship.customPages) !== null && _relationship_customPages !== void 0 ? _relationship_customPages : [], relationship.introVideo);
    const { extraClause } = relationship;
    const { buttonPrimary } = relationship.vendor.branding;
    const { qboClasses } = useQboClasses({
        refreshOnHookMount: true
    });
    const isMobileDevice = useIsMobileForHomePage();
    const { viewLegalTerms } = useViewLegalTerms({
        vendorBusinessId: relationship.vendor.id
    });
    const showQuillViewerModal = useShowQuillViewerModal();
    const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
    const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet({
        relationship,
        updatedNetTerms: updatedNetTerms.value(),
        texts: amendmentsTexts
    }) : null;
    const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
    const agreementData = AgreementDataModel.fromRelationship(relationship);
    const legalTerms = useMemo(()=>legalTermsModel.fromRelationshipLegalTerms(relationship.legalTermsDocuments), [
        relationship.legalTermsDocuments
    ]);
    const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet({
        relationship,
        coverCreditCardFees: updatedIsVendorCoveringCCFee.value(),
        texts: amendmentsTexts
    }) : null;
    const effectiveDateTerm = relationship.effectiveDate && /*#__PURE__*/ _jsx(SpecificTerm, {
        field: viewTexts.effectiveDateOfAgreement(),
        value: relationshipModel.formatEffectiveDate(relationship, onAcceptanceTexts),
        testId: autoprefix + 'effective-date'
    });
    const termsOfServiceList = useMemo(()=>{
        if (!legalTerms || legalTerms.length === 0) return null;
        const getName = (doc)=>{
            var _doc_name;
            return doc === null || doc === void 0 ? void 0 : (_doc_name = doc.name) === null || _doc_name === void 0 ? void 0 : _doc_name.replace(/\.pdf$/i, '');
        };
        const renderButton = (doc, text)=>{
            var _ref;
            return /*#__PURE__*/ _jsx(TextButton, {
                testId: "tos-".concat(doc === null || doc === void 0 ? void 0 : doc.id),
                bi: {
                    name: 'view_terms_of_service',
                    data: {
                        document_id: (_ref = doc === null || doc === void 0 ? void 0 : doc.id) !== null && _ref !== void 0 ? _ref : ''
                    }
                },
                onClick: ()=>viewLegalTerms(doc, relationship.id),
                children: text
            });
        };
        const value = legalTerms.length === 1 ? renderButton(legalTerms[0], viewTexts.view()) : legalTerms.map((doc, index, arr)=>/*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    renderButton(doc, getName(doc)),
                    index < arr.length - 1 && ', '
                ]
            }));
        return /*#__PURE__*/ _jsx(SpecificTerm, {
            testId: autoprefix + 'tos-list',
            field: viewTexts.termsOfService(),
            value: value
        });
    }, [
        legalTerms,
        relationship.id,
        viewLegalTerms
    ]);
    const serviceTermsEntries = useMemo(()=>{
        if (!featureFlags.SERVICE_LEVEL_TERMS) return [];
        return getServiceTermsEntries({
            relationship,
            services: relationship.services
        }).map((entry)=>({
                id: entry.serviceId,
                name: entry.title,
                terms: entry.description
            }));
    }, [
        relationship,
        featureFlags.SERVICE_LEVEL_TERMS
    ]);
    const serviceTermsList = useMemo(()=>{
        if (serviceTermsEntries.length === 0) return null;
        const viewServiceTerms = (entry)=>showQuillViewerModal({
                testId: autoprefix + 'service-terms-modal-' + entry.id,
                title: entry.name,
                value: entry.terms
            });
        const value = serviceTermsEntries.map((entry, index, arr)=>/*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(TextButton, {
                        testId: autoprefix + 'service-terms-' + entry.id,
                        bi: {
                            name: 'view_service_terms',
                            data: {
                                service_id: entry.id
                            }
                        },
                        onClick: ()=>viewServiceTerms(entry),
                        children: entry.name
                    }),
                    index < arr.length - 1 && ', '
                ]
            }, entry.id));
        return /*#__PURE__*/ _jsx(SpecificTerm, {
            testId: autoprefix + 'service-terms-list',
            field: viewTexts.serviceTerms(),
            value: value
        });
    }, [
        serviceTermsEntries,
        showQuillViewerModal
    ]);
    const termsAcceptedAtSigning = useMemo(()=>{
        var _relationship_legalTermsDocuments;
        const statements = (_relationship_legalTermsDocuments = relationship.legalTermsDocuments) === null || _relationship_legalTermsDocuments === void 0 ? void 0 : _relationship_legalTermsDocuments.reduce((acc, term)=>{
            var _term_termsOfService_explicitApproval;
            if (term.termsOfService.type === 'explicitApproval' && ((_term_termsOfService_explicitApproval = term.termsOfService.explicitApproval) === null || _term_termsOfService_explicitApproval === void 0 ? void 0 : _term_termsOfService_explicitApproval.statement)) {
                var _term_termsOfService_explicitApproval1;
                acc.push((_term_termsOfService_explicitApproval1 = term.termsOfService.explicitApproval) === null || _term_termsOfService_explicitApproval1 === void 0 ? void 0 : _term_termsOfService_explicitApproval1.statement);
            }
            return acc;
        }, []);
        if (!statements || statements.length === 0) return null;
        return /*#__PURE__*/ _jsx(SpecificTerm, {
            testId: autoprefix + 'terms-accepted-at-signing',
            field: viewTexts.termsExplicitlyAcceptedAtSigning(),
            value: statements === null || statements === void 0 ? void 0 : statements.map((statement, index)=>/*#__PURE__*/ _jsxs("div", {
                    className: styles.termsAcceptanceContainer,
                    children: [
                        /*#__PURE__*/ _jsx(Checkmark, {}),
                        /*#__PURE__*/ _jsx(Typography, {
                            className: styles.termsAcceptanceStatement,
                            weight: "light",
                            children: statement
                        }, "termsAcceptence-".concat(index, "-statement"))
                    ]
                }, "termsAcceptance-".concat(index, "-container")))
        });
    }, [
        relationship.legalTermsDocuments
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Header, {
                relationship: relationship,
                onUpdate: refresh
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.main,
                "data-testid": "agreement-view",
                children: [
                    /*#__PURE__*/ _jsx(ChangesSummary, {
                        isVendor: isVendor,
                        relationship: relationship,
                        testId: autoprefix + 'changes',
                        refresh: refresh
                    }),
                    ((_relationship_practiceManagementSyncFailures = relationship.practiceManagementSyncFailures) === null || _relationship_practiceManagementSyncFailures === void 0 ? void 0 : _relationship_practiceManagementSyncFailures.length) && /*#__PURE__*/ _jsx(PracticeManagementSyncFailures, {
                        relationship: relationship,
                        refresh: refresh
                    }),
                    /*#__PURE__*/ _jsx(SignedHeader, {
                        relationship: relationship,
                        brandColor: buttonPrimary,
                        testPrefix: autoprefix
                    }),
                    customPagesOn && /*#__PURE__*/ _jsx(CustomPagesInAgreement, {
                        relationship: relationship
                    }),
                    /*#__PURE__*/ _jsx(AgreementViewCard, {
                        headTitle: viewTexts.servicesProvided(),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: styles.services,
                            children: /*#__PURE__*/ _jsx(StatementOfWork, {
                                services: relationship.services,
                                isVendor: isVendor,
                                qboClasses: qboClasses,
                                serviceDetailsProps: {
                                    agreementData,
                                    isVendor,
                                    relationshipState: {
                                        type: 'approved',
                                        effectiveDate: relationshipModel.effectiveDate(relationship)
                                    }
                                }
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(AgreementViewCard, {
                        headTitle: viewTexts.agreementTerms(),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.specificTerms,
                            children: [
                                termsOfServiceList,
                                termsAcceptedAtSigning,
                                effectiveDateTerm,
                                /*#__PURE__*/ _jsx(SpecificTerm, {
                                    field: viewTexts.paymentTerms(),
                                    value: relationshipModel.paymentTermsText(relationship, paymentTermsTexts),
                                    testId: autoprefix + 'net-terms',
                                    changes: netTermsChangeBullet && /*#__PURE__*/ _jsx(AmendmentChangesInAgreementView, {
                                        disableBorderRadius: true,
                                        items: [
                                            netTermsChangeBullet
                                        ],
                                        autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
                                        testId: autoprefix + 'net-terms-changes'
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecificTerm, {
                                    testId: autoprefix + 'cc-fees',
                                    field: viewTexts.cardFees(),
                                    value: getCoverCCFeesText({
                                        coverCreditCardFees: relationshipModel.actualIsVendorCoveringCCFee(relationship),
                                        vendorBusinessName: relationshipModel.vendorName(relationship),
                                        clientBusinessName: relationshipModel.customerName(relationship),
                                        texts: amendmentsTexts
                                    }),
                                    changes: coverCCFeesChangeBullet && /*#__PURE__*/ _jsx(AmendmentChangesInAgreementView, {
                                        disableBorderRadius: true,
                                        items: [
                                            coverCCFeesChangeBullet
                                        ],
                                        autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
                                        testId: autoprefix + 'cc-fees-changes'
                                    })
                                }),
                                /*#__PURE__*/ _jsx(AgreementAmendmentsApprovalMethod, {
                                    relationship: relationship,
                                    testId: autoprefix
                                }),
                                /*#__PURE__*/ _jsx(AgreementInvoicesApprovalMethod, {
                                    relationship: relationship,
                                    testId: autoprefix
                                }),
                                extraClause && /*#__PURE__*/ _jsx(SpecificTerm, {
                                    field: viewTexts.notes(),
                                    value: /*#__PURE__*/ _jsx(RichTextViewer, {
                                        testId: 'extra-clause-rich-text-viewer',
                                        value: extraClause,
                                        skin: "secondary"
                                    })
                                }),
                                serviceTermsList
                            ]
                        })
                    }),
                    isMobileDevice && /*#__PURE__*/ _jsx(Box, {
                        className: styles.mobileBottomSpacer
                    })
                ]
            })
        ]
    });
}
